/**
 * tradeDeviceMeta.js
 * ---------------------------------------------------------------------------
 * Portable device/channel metadata for trade requests (web, PWA, Appilix app).
 *
 * COPY THIS SINGLE FILE to any React/Vanilla project.
 *
 * Quick start:
 *   1) On login / app boot:
 *        import { initTradeDeviceMeta } from './tradeDeviceMeta';
 *        initTradeDeviceMeta({ clientId: user.clientId });
 *
 *   2) Before each trade API call:
 *        import { getTradeDevicePayload } from './tradeDeviceMeta';
 *        const devicePayload = await getTradeDevicePayload({ clientId: user.clientId });
 *        fetch('/order', { method: 'POST', body: JSON.stringify({ ...order, ...devicePayload }) });
 *
 * Server side: pair with trade_device_meta.php (merge client deviceMeta + request headers).
 *
 * captureVersion 1.1 adds:
 *   device (summary), os/browser versions, UA Client Hints, GPU, storage, media device counts
 * ---------------------------------------------------------------------------
 */

const DEFAULTS = {
  storagePrefix: 'pt_device_',
  fcmTimeoutMs: 1500,
  prefetchFcm: true,
};

let config = { ...DEFAULTS };
let cachedFcmToken = null;
let fcmFetchPromise = null;

function safeGet(getter, fallback = null) {
  try {
    const value = getter();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

function storageKey(name) {
  return `${config.storagePrefix}${name}`;
}

function getOrCreateId(name) {
  const key = storageKey(name);
  try {
    let value = localStorage.getItem(key);
    if (!value) {
      value =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(key, value);
    }
    return value;
  } catch {
    return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readCookie(name) {
  const prefix = `${name}=`;
  const parts = (`; ${document.cookie}`).split(`; ${prefix}`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift() || '';
  }
  return '';
}

function matchFirst(text, patterns) {
  for (let i = 0; i < patterns.length; i += 1) {
    const match = text.match(patterns[i]);
    if (match && match[1]) {
      return match[1];
    }
  }
  return '';
}

function normalizeOsVersion(raw) {
  if (!raw) return '';
  return String(raw).replace(/_/g, '.');
}

function parseUserAgent(ua) {
  const text = ua || '';
  let osFamily = 'unknown';
  let osVersion = '';

  if (/Windows NT/i.test(text)) {
    osFamily = 'Windows';
    const winMap = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    const nt = matchFirst(text, [/Windows NT ([0-9.]+)/i]);
    osVersion = winMap[nt] || nt;
  } else if (/Android/i.test(text)) {
    osFamily = 'Android';
    osVersion = matchFirst(text, [/Android ([0-9.]+)/i]);
  } else if (/iPhone|iPad|iPod/i.test(text)) {
    osFamily = 'iOS';
    osVersion = normalizeOsVersion(matchFirst(text, [/OS ([0-9_]+)/i]));
  } else if (/Mac OS X|Macintosh/i.test(text)) {
    osFamily = 'macOS';
    osVersion = normalizeOsVersion(matchFirst(text, [/Mac OS X ([0-9_]+)/i]));
  } else if (/Linux/i.test(text)) {
    osFamily = 'Linux';
  }

  let browserFamily = 'unknown';
  let browserVersion = '';
  if (/Edg\//i.test(text)) {
    browserFamily = 'Edge';
    browserVersion = matchFirst(text, [/Edg\/([0-9.]+)/i]);
  } else if (/Chrome/i.test(text) && !/Edg/i.test(text)) {
    browserFamily = 'Chrome';
    browserVersion = matchFirst(text, [/Chrome\/([0-9.]+)/i]);
  } else if (/Firefox/i.test(text)) {
    browserFamily = 'Firefox';
    browserVersion = matchFirst(text, [/Firefox\/([0-9.]+)/i]);
  } else if (/Safari/i.test(text) && !/Chrome/i.test(text)) {
    browserFamily = 'Safari';
    browserVersion = matchFirst(text, [/Version\/([0-9.]+)/i, /Safari\/([0-9.]+)/i]);
  }

  const isWebView =
    /; wv\)/i.test(text) ||
    (typeof window !== 'undefined' && typeof window.appilix !== 'undefined') ||
    (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined');

  // Android model often appears as "...; SM-S911B Build/..."
  const modelFromUa = matchFirst(text, [
    /;\s*([^;)]+?)\s+Build\//i,
    /;\s*([A-Z0-9-]{3,})\s*;\s*wv\)/i,
  ]);

  return { osFamily, osVersion, browserFamily, browserVersion, isWebView, modelFromUa };
}

async function collectUaClientHints() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const uad = nav.userAgentData;
  if (!uad) {
    return null;
  }

  const base = {
    mobile: Boolean(uad.mobile),
    platform: uad.platform || '',
    brands: Array.isArray(uad.brands)
      ? uad.brands.map((b) => ({ brand: b.brand || '', version: b.version || '' }))
      : [],
    architecture: '',
    bitness: '',
    model: '',
    platformVersion: '',
    fullVersionList: [],
  };

  if (typeof uad.getHighEntropyValues !== 'function') {
    return base;
  }

  try {
    const high = await uad.getHighEntropyValues([
      'architecture',
      'bitness',
      'model',
      'platformVersion',
      'fullVersionList',
    ]);
    base.architecture = high.architecture || '';
    base.bitness = high.bitness || '';
    base.model = high.model || '';
    base.platformVersion = high.platformVersion || '';
    base.fullVersionList = Array.isArray(high.fullVersionList)
      ? high.fullVersionList.map((b) => ({ brand: b.brand || '', version: b.version || '' }))
      : [];
  } catch {
    // High-entropy hints may be blocked; keep low-entropy values.
  }

  return base;
}

function collectGpuInfo() {
  try {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');
    if (!gl) return null;

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);

    return {
      vendor: vendor || '',
      renderer: renderer || '',
    };
  } catch {
    return null;
  }
}

async function collectStorageEstimate() {
  try {
    if (!navigator.storage || typeof navigator.storage.estimate !== 'function') {
      return null;
    }
    const estimate = await navigator.storage.estimate();
    return {
      quota: estimate.quota || 0,
      usage: estimate.usage || 0,
    };
  } catch {
    return null;
  }
}

async function collectMediaDeviceCounts() {
  try {
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
      return null;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const counts = { audioinput: 0, audiooutput: 0, videoinput: 0, other: 0 };
    devices.forEach((d) => {
      if (Object.prototype.hasOwnProperty.call(counts, d.kind)) {
        counts[d.kind] += 1;
      } else {
        counts.other += 1;
      }
    });
    return counts;
  } catch {
    return null;
  }
}

function buildDeviceSummary({
  formFactor,
  parsedUa,
  uaHints,
  gpu,
  nav,
  installId,
  fcmToken,
  isAppilix,
}) {
  const hintPlatformVersion = uaHints && uaHints.platformVersion ? uaHints.platformVersion : '';
  const model =
    (uaHints && uaHints.model) ||
    parsedUa.modelFromUa ||
    '';

  return {
    formFactor,
    os: {
      family: parsedUa.osFamily,
      version: hintPlatformVersion || parsedUa.osVersion || '',
    },
    browser: {
      family: parsedUa.browserFamily,
      version: parsedUa.browserVersion || '',
    },
    uaHints: uaHints
      ? {
          platform: uaHints.platform || '',
          platformVersion: uaHints.platformVersion || '',
          architecture: uaHints.architecture || '',
          bitness: uaHints.bitness || '',
          model: uaHints.model || '',
          mobile: Boolean(uaHints.mobile),
          brands: uaHints.brands || [],
        }
      : null,
    model,
    gpu: gpu ? gpu.renderer || '' : '',
    gpuVendor: gpu ? gpu.vendor || '' : '',
    ramGb: nav.deviceMemory || 0,
    cpuCores: nav.hardwareConcurrency || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    installId,
    fcmToken: fcmToken || '',
    appilix: Boolean(isAppilix),
  };
}

function detectRuntime() {
  if (typeof window === 'undefined') return 'unknown';
  if (typeof window.appilix !== 'undefined') return 'appilix_app';
  if (window.ReactNativeWebView) return 'react_native_webview';
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return 'pwa';
    if (window.navigator && window.navigator.standalone === true) return 'pwa';
  } catch {
    // ignore
  }
  const ua = safeGet(() => navigator.userAgent, '');
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return 'mobile_browser';
  return 'desktop_browser';
}

function detectPlatformType(ua, maxTouchPoints) {
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobile|iPhone|Android/i.test(ua)) return 'mobile';
  if ((maxTouchPoints || 0) > 1 && /Macintosh/i.test(ua)) return 'tablet';
  return 'desktop';
}

function isStandalonePwa() {
  return detectRuntime() === 'pwa';
}

function collectConnectionMeta() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (!conn) return null;
  return {
    effectiveType: conn.effectiveType || '',
    downlink: conn.downlink || 0,
    rtt: conn.rtt || 0,
    saveData: Boolean(conn.saveData),
  };
}

function simpleFingerprint(parts) {
  const raw = JSON.stringify(parts);
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `fp_${Math.abs(hash)}`;
}

function getAppilixBridge() {
  return typeof window !== 'undefined' && window.appilix ? window.appilix : null;
}

function recordAppilixUserIdentity(clientId) {
  const bridge = getAppilixBridge();
  if (!bridge || !clientId) return;
  try {
    bridge.postMessage(
      JSON.stringify({
        type: 'firebase_record_user_identity',
        props: { user_identity: String(clientId) },
      })
    );
  } catch {
    // ignore bridge errors
  }
}

function fetchAppilixFcmToken(timeoutMs) {
  const bridge = getAppilixBridge();
  if (!bridge) return Promise.resolve('');

  if (cachedFcmToken) return Promise.resolve(cachedFcmToken);
  if (fcmFetchPromise) return fcmFetchPromise;

  fcmFetchPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (token) => {
      if (settled) return;
      settled = true;
      fcmFetchPromise = null;
      const normalized = token ? String(token) : '';
      if (normalized) {
        cachedFcmToken = normalized;
        try {
          localStorage.setItem(storageKey('fcm_token'), normalized);
        } catch {
          // ignore
        }
      }
      resolve(normalized);
    };

    const timer = setTimeout(() => finish(''), timeoutMs);

    const previousHandler = bridge.onmessage;
    bridge.onmessage = function appilixFcmHandler(event) {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'firebase_token') {
          clearTimeout(timer);
          bridge.onmessage = previousHandler || null;
          finish(data.token || '');
        }
      } catch {
        // ignore malformed payloads
      }
    };

    try {
      bridge.postMessage(JSON.stringify({ type: 'firebase_token' }));
    } catch {
      clearTimeout(timer);
      bridge.onmessage = previousHandler || null;
      finish('');
    }
  });

  return fcmFetchPromise;
}

/**
 * Call once after login or on app startup (optional but recommended for Appilix FCM cache).
 */
export function initTradeDeviceMeta(options = {}) {
  config = { ...DEFAULTS, ...options };
  const clientId = options.clientId || null;

  if (clientId) {
    recordAppilixUserIdentity(clientId);
  }

  try {
    const savedFcm = localStorage.getItem(storageKey('fcm_token'));
    if (savedFcm) cachedFcmToken = savedFcm;
  } catch {
    // ignore
  }

  if (config.prefetchFcm && getAppilixBridge()) {
    fetchAppilixFcmToken(config.fcmTimeoutMs).catch(() => {});
  }
}

/**
 * Returns metadata object to store with a trade.
 */
export async function getTradeDeviceMeta(options = {}) {
  const merged = { ...DEFAULTS, ...config, ...options };
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const screenObj = typeof window !== 'undefined' ? window.screen : {};
  const ua = safeGet(() => nav.userAgent, '');
  const parsedUa = parseUserAgent(ua);
  const runtime = detectRuntime();
  const clientId = merged.clientId || null;
  const formFactor = detectPlatformType(ua, nav.maxTouchPoints || 0);
  const installId = getOrCreateId('install_id');

  const [uaHints, storage, mediaDevices] = await Promise.all([
    collectUaClientHints(),
    collectStorageEstimate(),
    collectMediaDeviceCounts(),
  ]);
  const gpu = collectGpuInfo();

  let fcmToken = '';
  if (runtime === 'appilix_app') {
    fcmToken = cachedFcmToken || '';
    try {
      fcmToken = fcmToken || localStorage.getItem(storageKey('fcm_token')) || '';
    } catch {
      // ignore
    }
    if (merged.includeFcm !== false && !fcmToken) {
      fcmToken = await fetchAppilixFcmToken(merged.fcmTimeoutMs || DEFAULTS.fcmTimeoutMs);
    }
  }

  const device = buildDeviceSummary({
    formFactor,
    parsedUa,
    uaHints,
    gpu,
    nav,
    installId,
    fcmToken,
    isAppilix: runtime === 'appilix_app',
  });

  const meta = {
    captureVersion: '1.1',
    capturedAt: new Date().toISOString(),
    client: {
      clientId,
      sessionId: getOrCreateId('session_id'),
      installId,
      tradeAttemptId:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `trade_${Date.now()}`,
    },
    channel: {
      runtime,
      platformType: formFactor,
      osFamily: parsedUa.osFamily,
      osVersion: device.os.version,
      browserFamily: parsedUa.browserFamily,
      browserVersion: device.browser.version,
      isAppilix: runtime === 'appilix_app',
      isStandalonePwa: isStandalonePwa(),
      isWebView: parsedUa.isWebView,
      orderDevice: 1,
    },
    device,
    locale: {
      timezone: safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ''),
      timezoneOffset: new Date().getTimezoneOffset(),
      language: nav.language || '',
      languages: safeGet(() => Array.from(nav.languages || []), []),
    },
    display: {
      screen: {
        width: screenObj.width || 0,
        height: screenObj.height || 0,
        availWidth: screenObj.availWidth || 0,
        availHeight: screenObj.availHeight || 0,
        colorDepth: screenObj.colorDepth || 0,
      },
      viewport: {
        innerWidth: safeGet(() => window.innerWidth, 0),
        innerHeight: safeGet(() => window.innerHeight, 0),
        devicePixelRatio: safeGet(() => window.devicePixelRatio, 1),
        orientation: safeGet(() => screenObj.orientation.type, ''),
      },
    },
    browser: {
      userAgent: ua,
      platform: nav.platform || '',
      vendor: nav.vendor || '',
      online: safeGet(() => nav.onLine, true),
      hardwareConcurrency: nav.hardwareConcurrency || 0,
      deviceMemory: nav.deviceMemory || 0,
      maxTouchPoints: nav.maxTouchPoints || 0,
      webdriver: Boolean(nav.webdriver),
      pageUrl: safeGet(() => window.location.href, ''),
      referrer: safeGet(() => document.referrer, ''),
    },
    gpu,
    uaHints,
    storage,
    mediaDevices,
    network: collectConnectionMeta(),
    fingerprint: simpleFingerprint({
      ua,
      tz: safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ''),
      screen: [screenObj.width, screenObj.height],
      dpr: safeGet(() => window.devicePixelRatio, 1),
      installId,
      gpu: device.gpu,
      model: device.model,
      arch: device.uaHints ? device.uaHints.architecture : '',
    }),
    appilix: null,
  };

  if (meta.channel.isAppilix) {
    meta.appilix = {
      bridgeAvailable: true,
      fcmToken: fcmToken || '',
      userIdentity:
        readCookie('appilix_push_notification_user_identity') ||
        (clientId ? String(clientId) : ''),
    };
  }

  return meta;
}

/**
 * Convenience wrapper for order payloads: { deviceMeta: {...} }
 */
export async function getTradeDevicePayload(options = {}) {
  return {
    deviceMeta: await getTradeDeviceMeta(options),
  };
}

export default {
  initTradeDeviceMeta,
  getTradeDeviceMeta,
  getTradeDevicePayload,
};
